import { getApiClient } from "@/services"
import {
  type AttachedWidget,
  createAgentTextMessage,
  createErrorMessage,
  createToolCallMessage,
  createUserMessage,
  type Message,
} from "@/types"
import { useCallback, useEffect, useReducer, useRef, useState } from "react"

import { CARE_ADVOCATE_PHONE_LINK, PENDO_AGENT_ID } from "@/utils/constants"
import { chatReducer, initialState } from "./useChat.reducer"
import { usePendo } from "./usePendo"

export const GENERIC_ERROR_MESSAGE =
  "Apologies — something’s not working on my end. Give it another try, or if you’d " +
  `rather not wait, a Care Advocate is available at ${CARE_ADVOCATE_PHONE_LINK} or via Live Chat.`

export const NON_RETRYABLE_ERROR_MESSAGE =
  "Apologies — something’s not working on my end. A Care Advocate is available to help " +
  `at ${CARE_ADVOCATE_PHONE_LINK} or via Live Chat.`

interface UseChatOptions {
  getAuthToken?: () => string | null;
  onWidgetAction?: (action: string, data: Record<string, unknown>) => void;
  enabled?: boolean;
}

interface UseChatReturn {
  messages: Message[];
  isLoading: boolean;
  error: string | null;
  threadId: string | null;
  sendMessage: (content: string) => Promise<void>;
  clearMessages: () => void;
  handleWidgetAction: (action: string, data: Record<string, unknown>) => void;
  abortStream: () => void;
  voteOnMessage: (
    messageId: string,
    wasHelpful: boolean | null,
  ) => Promise<void>;
  isVoting: boolean;
}

/**
 * Hook for managing chat state and interactions with streaming support
 */
export function useChat(options: UseChatOptions = {}): UseChatReturn {
  const { getAuthToken = () => null, onWidgetAction, enabled = true } = options
  const [state, dispatch] = useReducer(chatReducer, initialState)
  const apiClientRef = useRef(getApiClient(getAuthToken))
  const abortControllerRef = useRef<AbortController | null>(null)
  const hasInitializedRef = useRef(false)
  const { trackAgent } = usePendo()

  // Create thread on load and display greeting
  useEffect(() => {
    if (!enabled || hasInitializedRef.current) return
    hasInitializedRef.current = true

    const initThread = async () => {
      dispatch({ type: "SET_LOADING", payload: true })
      try {
        const thread = await apiClientRef.current.createThread()
        dispatch({ type: "SET_THREAD_ID", payload: thread.id })
        if (thread.greeting) {
          dispatch({
            type: "ADD_MESSAGE",
            payload: createAgentTextMessage(thread.greeting),
          })
        }
      } catch (error) {
        console.error("Failed to initialize chat thread:", error)

        dispatch({
          type: "ADD_MESSAGE",
          payload: createErrorMessage(NON_RETRYABLE_ERROR_MESSAGE),
        })
      } finally {
        dispatch({ type: "SET_LOADING", payload: false })
      }
    }

    initThread()
  }, [enabled])

  const sendMessage = useCallback(
    async (content: string) => {
      // Don't send empty messages
      if (!content.trim()) return

      // Thread should be created on init; bail if not ready
      if (!state.threadId) {
        dispatch({
          type: "ADD_MESSAGE",
          payload: createErrorMessage(NON_RETRYABLE_ERROR_MESSAGE),
        })
        return
      }

      // Abort any existing stream
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      abortControllerRef.current = new AbortController()

      // Add user message immediately
      const userMessage = createUserMessage(content)
      dispatch({ type: "ADD_MESSAGE", payload: userMessage })
      dispatch({ type: "SET_LOADING", payload: true })

      try {
        const threadId = state.threadId

        // Track prompt event
        const promptMessageId = crypto.randomUUID()
        trackAgent("prompt", {
          agentId: PENDO_AGENT_ID,
          conversationId: threadId,
          messageId: promptMessageId,
          content,
        })

        // Stream the response
        const stream = apiClientRef.current.sendMessageStream(
          threadId,
          content,
          abortControllerRef.current.signal,
        )

        let serverId: string | undefined
        let widgets: AttachedWidget[] | undefined
        const contentBuffer: string[] = []
        let hadError = false

        for await (const event of stream) {
          switch (event.t) {
            case "text":
              contentBuffer.push(event.body)
              break

            case "tool_call":
              dispatch({
                type: "ADD_MESSAGE",
                payload: createToolCallMessage(event.body),
              })
              break

            case "complete":
              // Final event with message_id and optional widgets
              serverId = event.body.message_id?.toString()
              if (event.body.widgets) {
                widgets = event.body.widgets.map((w) => ({
                  widgetType: w.widget_type,
                  data: w.data,
                }))
              }

              // Don't emit a bubble after an error event — complete is just a stream terminator
              if (!hadError) {
                const responseContent = contentBuffer.join("")
                if (responseContent.trim() === "") {
                  dispatch({
                    type: "ADD_MESSAGE",
                    payload: createErrorMessage(NON_RETRYABLE_ERROR_MESSAGE),
                  })
                } else {
                  // Track agent response event
                  trackAgent("agent_response", {
                    agentId: PENDO_AGENT_ID,
                    conversationId: threadId,
                    messageId: serverId ?? crypto.randomUUID(),
                    content: responseContent,
                  })

                  dispatch({
                    type: "ADD_MESSAGE",
                    payload: createAgentTextMessage(
                      responseContent,
                      serverId,
                      widgets,
                    ),
                  })
                }
              }

              dispatch({ type: "SET_LOADING", payload: false })

              break

            case "suggestions":
              // Arrives after `complete`; attach chips to that message.
              if (event.body.chips.length > 0) {
                dispatch({
                  type: "SET_MESSAGE_SUGGESTIONS",
                  payload: {
                    serverId: event.body.message_id.toString(),
                    chips: event.body.chips,
                  },
                })
              }
              break

            case "error":
              hadError = true
              dispatch({
                type: "ADD_MESSAGE",
                payload: createErrorMessage(event.body),
              })
              dispatch({ type: "SET_ERROR", payload: event.body })
              break
          }
        }
      } catch (error) {
        // Check if it was an abort
        if (error instanceof Error && error.name === "AbortError") {
          return
        }

        console.error("Error processing message stream:", error)

        dispatch({
          type: "ADD_MESSAGE",
          payload: createErrorMessage(GENERIC_ERROR_MESSAGE),
        })
        dispatch({ type: "SET_ERROR", payload: GENERIC_ERROR_MESSAGE })
      } finally {
        dispatch({ type: "SET_LOADING", payload: false })
        abortControllerRef.current = null
      }
    },
    [state.threadId, trackAgent],
  )

  const clearMessages = useCallback(() => {
    // Abort any existing stream
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    dispatch({ type: "CLEAR_MESSAGES" })
  }, [])

  const abortStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
      dispatch({ type: "SET_LOADING", payload: false })
    }
  }, [])

  const handleWidgetAction = useCallback(
    (action: string, data: Record<string, unknown>) => {
      // Call custom handler if provided
      onWidgetAction?.(action, data)
    },
    [onWidgetAction],
  )

  // Voting state and function
  const [isVoting, setIsVoting] = useState(false)

  // Ref lets voteOnMessage read current messages without depending on them
  const messagesRef = useRef(state.messages)
  messagesRef.current = state.messages

  const voteOnMessage = useCallback(
    async (messageId: string, wasHelpful: boolean | null) => {
      const message = messagesRef.current.find(
        (m) => m.id === messageId && m.type === "text" && m.sender === "agent",
      )
      if (!message || message.type !== "text" || !message.serverId) {
        console.warn("Cannot vote on message without server ID")
        return
      }

      if (!state.threadId) {
        console.warn("Cannot vote without thread ID")
        return
      }

      setIsVoting(true)
      try {
        await apiClientRef.current.voteOnMessage(
          state.threadId,
          message.serverId,
          wasHelpful,
        )
        dispatch({
          type: "SET_MESSAGE_VOTE",
          payload: { id: messageId, wasHelpful },
        })

        // Track user reaction event
        const reaction =
          wasHelpful === true
            ? "positive"
            : wasHelpful === false
              ? "negative"
              : "undo"
        trackAgent("user_reaction", {
          agentId: PENDO_AGENT_ID,
          conversationId: state.threadId!,
          messageId: message.serverId,
          content: reaction,
        })
      } catch (error) {
        console.error("Failed to vote on message:", error)
      } finally {
        setIsVoting(false)
      }
    },
    [state.threadId, trackAgent],
  )

  return {
    messages: state.messages,
    isLoading: state.isLoading,
    error: state.error,
    threadId: state.threadId,
    sendMessage,
    clearMessages,
    handleWidgetAction,
    abortStream,
    voteOnMessage,
    isVoting,
  }
}
